import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, Button, Badge, Input, Spinner } from '@tkpod/ui';
import { api } from '@/lib/api';
import type {
  OptionTypeDto,
  OptionValueDto,
  UpdateOptionTypeRequest,
  CreateOptionValueRequest,
  UpdateOptionValueRequest,
  OptionUIType,
  MetadataFieldDef,
} from '@tkpod/shared';
import { isValidHex6, resolveColorNameToHex } from '@tkpod/shared';
import { AlertBanner, useAutoHideMessage } from '@/components/AlertBanner';
import { UI_TYPE_BADGE, UI_TYPE_OPTIONS } from '@/components/catalog/option-constants';
import { DynamicMetadataForm } from '@/components/catalog/DynamicMetadataForm';
import { DynamicValuesTable } from '@/components/catalog/DynamicValuesTable';
import { MetadataSchemaEditor } from '@/components/catalog/MetadataSchemaEditor';
import { getEffectiveSchema } from '@/components/catalog/default-metadata-schema';

export function VariantOptionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [valueModal, setValueModal] = useState<{ mode: 'create' } | { mode: 'edit'; value: OptionValueDto } | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<OptionValueDto | null>(null);
  const [pageSuccess, setPageSuccess] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const visibleSuccess = useAutoHideMessage(pageSuccess);
  const visibleError = useAutoHideMessage(pageError);

  const { data: optionType, isLoading } = useQuery<OptionTypeDto>({
    queryKey: ['option-type', id],
    queryFn: () => api.get<OptionTypeDto>(`/api/v1/admin/options/types/${id}`),
    enabled: !!id,
  });

  const { data: values = [], isLoading: valuesLoading } = useQuery<OptionValueDto[]>({
    queryKey: ['option-type-values', id],
    queryFn: () => api.get<OptionValueDto[]>(`/api/v1/admin/options/types/${id}/values`),
    enabled: !!id,
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['option-type-values', id] });
    queryClient.invalidateQueries({ queryKey: ['option-type', id] });
    queryClient.invalidateQueries({ queryKey: ['option-types'] });
  };

  const reorderMutation = useMutation({
    mutationFn: (orderedIds: string[]) =>
      api.patch(`/api/v1/admin/options/types/${id}/values/reorder`, { orderedIds }),
    onSuccess: () => invalidateAll(),
    onError: (err: Error) => {
      setPageSuccess(null);
      setPageError(err.message || 'Failed to reorder values.');
      invalidateAll();
    },
  });

  const handleReorder = (orderedIds: string[]) => {
    queryClient.setQueryData<OptionValueDto[]>(['option-type-values', id], (old) => {
      if (!old) return old;
      const map = new Map(old.map((v) => [v.id, v]));
      return orderedIds
        .map((vid) => map.get(vid))
        .filter((v): v is OptionValueDto => !!v);
    });
    reorderMutation.mutate(orderedIds);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!optionType) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-sm text-text-tertiary">
        <p>Option type not found.</p>
        <Link to="/variant-options" className="mt-2 text-brand-600 hover:underline">Back to Variant Options</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-text-secondary">
        <Link to="/variant-options" className="hover:text-brand-600">Variant Options</Link>
        <span>/</span>
        <span className="text-text-primary">{optionType.name}</span>
      </div>

      {visibleSuccess && <AlertBanner type="success" message={visibleSuccess} />}
      {visibleError && <AlertBanner type="error" message={visibleError} />}

      <OptionTypeInfoCard
        key={optionType.id}
        optionType={optionType}
        onSaved={() => setPageSuccess('Option type updated successfully.')}
      />

      <Card>
        <CardHeader
          title="Values"
          description={`Manage values for the "${optionType.name}" option type. Drag to reorder — this order is shown on the storefront.`}
          action={
            <Button size="sm" onClick={() => setValueModal({ mode: 'create' })}>
              Add Value
            </Button>
          }
        />

        {valuesLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : values.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-sm text-text-tertiary">
            No values yet. Click "Add Value" to create one.
          </div>
        ) : (
          <DynamicValuesTable
            values={values}
            metadataSchema={getEffectiveSchema(optionType.metadataSchema as MetadataFieldDef[], optionType.uiType)}
            onEdit={(v) => setValueModal({ mode: 'edit', value: v })}
            onDelete={(v) => setDeleteDialog(v)}
            onReorder={handleReorder}
          />
        )}
      </Card>

      {valueModal && (
        <ValueFormModal
          mode={valueModal.mode}
          value={valueModal.mode === 'edit' ? valueModal.value : undefined}
          metadataSchema={getEffectiveSchema(optionType.metadataSchema as MetadataFieldDef[], optionType.uiType)}
          uiType={optionType.uiType}
          typeId={optionType.id}
          onClose={() => setValueModal(null)}
          onSuccess={(msg) => {
            invalidateAll();
            setValueModal(null);
            setPageSuccess(msg);
          }}
        />
      )}

      {deleteDialog && (
        <DeleteValueDialog
          value={deleteDialog}
          onClose={() => setDeleteDialog(null)}
          onSuccess={() => {
            invalidateAll();
            setDeleteDialog(null);
            setPageSuccess(`"${deleteDialog.name}" deleted successfully.`);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Option Type Info Card (editable)
// ---------------------------------------------------------------------------

function OptionTypeInfoCard({ optionType, onSaved }: { optionType: OptionTypeDto; onSaved: () => void }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(optionType.name);
  const [displayName, setDisplayName] = useState(optionType.displayName);
  const [description, setDescription] = useState(optionType.description ?? '');
  const [uiType, setUiType] = useState<OptionUIType>(optionType.uiType);
  const [metadataSchema, setMetadataSchema] = useState<MetadataFieldDef[]>(
    getEffectiveSchema(optionType.metadataSchema as MetadataFieldDef[], optionType.uiType),
  );
  const [sortOrder, setSortOrder] = useState(optionType.sortOrder);
  const [isActive, setIsActive] = useState(optionType.isActive);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) {
      setName(optionType.name);
      setDisplayName(optionType.displayName);
      setDescription(optionType.description ?? '');
      setUiType(optionType.uiType);
      setMetadataSchema(getEffectiveSchema(optionType.metadataSchema as MetadataFieldDef[], optionType.uiType));
      setSortOrder(optionType.sortOrder);
      setIsActive(optionType.isActive);
      setError(null);
    }
  }, [optionType, editing]);

  const resetForm = () => {
    setName(optionType.name);
    setDisplayName(optionType.displayName);
    setDescription(optionType.description ?? '');
    setUiType(optionType.uiType);
    setMetadataSchema(getEffectiveSchema(optionType.metadataSchema as MetadataFieldDef[], optionType.uiType));
    setSortOrder(optionType.sortOrder);
    setIsActive(optionType.isActive);
    setError(null);
  };

  const updateMutation = useMutation({
    mutationFn: (body: UpdateOptionTypeRequest) =>
      api.patch<OptionTypeDto>(`/api/v1/admin/options/types/${optionType.id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['option-type', optionType.id] });
      queryClient.invalidateQueries({ queryKey: ['option-types'] });
      setEditing(false);
      setError(null);
      onSaved();
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to update option type.');
    },
  });

  const handleSave = () => {
    setError(null);
    updateMutation.mutate({
      name: name.trim(),
      displayName: displayName.trim(),
      description: description.trim() || null,
      uiType,
      metadataSchema,
      sortOrder,
      isActive,
    });
  };

  return (
    <Card>
      <CardHeader
        title={optionType.name}
        description={`${optionType.uiType.replace(/_/g, ' ')} · ${optionType.isActive ? 'Active' : 'Inactive'} · ${optionType.valueCount} values`}
        action={
          !editing ? (
            <Button size="sm" variant="outline" onClick={() => { resetForm(); setEditing(true); }}>
              Edit
            </Button>
          ) : undefined
        }
      />
      {!editing && (
        <div className="flex items-center gap-2 px-6 pb-4">
          <Badge variant={UI_TYPE_BADGE[optionType.uiType] ?? 'default'} size="sm">
            {optionType.uiType.replace(/_/g, ' ')}
          </Badge>
          <Badge variant={optionType.isActive ? 'success' : 'default'} size="sm">
            {optionType.isActive ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      )}

      {editing && (
        <div className="space-y-4 px-6 pb-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-text-secondary">Internal Name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
              <p className="mt-1 text-xs text-text-tertiary">
                Unique across option types. Never shown to customers.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-text-secondary">Display Name</label>
              <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              <p className="mt-1 text-xs text-text-tertiary">
                Shown on Shopify and in the designer. Does not need to be unique.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-text-secondary">UI Type</label>
              <select
                value={uiType}
                onChange={(e) => {
                  const newUiType = e.target.value as OptionUIType;
                  setUiType(newUiType);
                  setMetadataSchema(getEffectiveSchema(optionType.metadataSchema as MetadataFieldDef[], newUiType));
                }}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                {UI_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-text-secondary">Sort Order</label>
              <Input type="number" value={String(sortOrder)} onChange={(e) => setSortOrder(Number(e.target.value))} />
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                role="switch"
                aria-checked={isActive}
                onClick={() => setIsActive(!isActive)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                  isActive ? 'bg-success' : 'bg-border-strong'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                    isActive ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
              <label className="text-sm font-medium text-text-secondary">
                {isActive ? 'Active' : 'Inactive'}
              </label>
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-text-secondary">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <MetadataSchemaEditor
              schema={metadataSchema}
              onChange={setMetadataSchema}
              uiType={uiType}
              disabled={updateMutation.isPending}
            />
          </div>

          {displayName.trim() !== optionType.displayName &&
            optionType.usedByTemplateCount > 0 && (
              <div className="rounded-md border border-warning/30 bg-warning-light p-3 text-sm text-warning-dark">
                This option type is used by {optionType.usedByTemplateCount}{' '}
                product template{optionType.usedByTemplateCount > 1 ? 's' : ''}.
                Changing the display name only affects future publishes — already
                published Shopify products keep the old option name until they are
                republished.
              </div>
            )}

          {error && <AlertBanner type="error" message={error} />}

          <div className="flex justify-end gap-3">
            <Button size="sm" variant="outline" disabled={updateMutation.isPending} onClick={() => { resetForm(); setEditing(false); }}>
              Cancel
            </Button>
            <Button size="sm" disabled={updateMutation.isPending} onClick={handleSave}>
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Color Library search (COLOR_SWATCH values only)
// ---------------------------------------------------------------------------

interface ColorSearchResult {
  id: string;
  name: string;
  hexCode: string;
  hexCode2: string | null;
  paletteId: string;
  paletteName: string;
}

/**
 * Autocomplete over the DB color library (admin/colors/search). Picking a
 * result fills name, display name and hex metadata in one click — the admin
 * can still edit any field afterwards.
 */
function ColorLibrarySearch({
  onPick,
  disabled,
}: {
  onPick: (entry: ColorSearchResult) => void;
  disabled: boolean;
}) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const { data: results = [], isFetching } = useQuery<ColorSearchResult[]>({
    queryKey: ['color-library-search', debounced],
    queryFn: () =>
      api.get<ColorSearchResult[]>(
        `/api/v1/admin/colors/search?q=${encodeURIComponent(debounced)}`,
      ),
    enabled: debounced.length >= 2,
    staleTime: 30_000,
  });

  const showDropdown = open && debounced.length >= 2;

  return (
    <div className="relative">
      <Input
        value={query}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search color library (e.g. Forest Green)"
      />
      {showDropdown && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded-md border border-border bg-surface shadow-lg">
          {isFetching && results.length === 0 ? (
            <div className="px-3 py-2 text-xs text-text-tertiary">Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-xs text-text-tertiary">
              No library colors match &quot;{debounced}&quot;.
            </div>
          ) : (
            results.map((entry) => (
              <button
                key={entry.id}
                type="button"
                // onMouseDown fires before the input's onBlur closes the list.
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPick(entry);
                  setQuery('');
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-surface-secondary"
              >
                <span
                  className="inline-block h-5 w-5 shrink-0 rounded border border-border shadow-sm"
                  style={
                    entry.hexCode2
                      ? { background: `linear-gradient(135deg, ${entry.hexCode} 50%, ${entry.hexCode2} 50%)` }
                      : { background: entry.hexCode }
                  }
                />
                <span className="flex-1 truncate text-text-primary">{entry.name}</span>
                <span className="font-mono text-xs text-text-tertiary">{entry.hexCode}</span>
                <span className="max-w-[110px] truncate text-xs text-text-tertiary">
                  {entry.paletteName}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Value Form Modal — dynamic metadata form based on metadataSchema
// ---------------------------------------------------------------------------

function ValueFormModal({
  mode,
  value,
  metadataSchema,
  uiType,
  typeId,
  onClose,
  onSuccess,
}: {
  mode: 'create' | 'edit';
  value?: OptionValueDto;
  metadataSchema: MetadataFieldDef[];
  uiType: OptionUIType;
  typeId: string;
  onClose: () => void;
  onSuccess: (message: string) => void;
}) {
  const isEdit = mode === 'edit' && value;
  const isColorSwatch = uiType === 'COLOR_SWATCH';

  const [name, setName] = useState(value?.name ?? '');
  const [displayName, setDisplayName] = useState(value?.displayName ?? '');
  const [metadata, setMetadata] = useState<Record<string, unknown>>(
    (value?.metadata ?? {}) as Record<string, unknown>,
  );
  const [error, setError] = useState<string | null>(null);

  // For COLOR_SWATCH option values we auto-fill `metadata.hexCode` from the
  // shared color-name map whenever the field is empty (or stores an invalid
  // value). User-supplied valid hex always wins — once it's a real #RRGGBB
  // we never overwrite it on subsequent name edits, so manual overrides are
  // sticky. A small badge below the hex input tells the admin when the value
  // came from the auto-detect map vs. their own entry.
  const handleNameChange = (val: string) => {
    setName(val);
    if (!displayName || displayName === name) setDisplayName(val);
    if (isColorSwatch) {
      const currentHex = metadata.hexCode;
      if (!isValidHex6(currentHex)) {
        const auto = resolveColorNameToHex(val);
        if (auto) {
          setMetadata((m) => ({ ...m, hexCode: auto }));
        }
      }
    }
  };

  const hexAutoFilled =
    isColorSwatch &&
    isValidHex6(metadata.hexCode) &&
    resolveColorNameToHex(name) === metadata.hexCode;

  const createMutation = useMutation({
    mutationFn: (body: CreateOptionValueRequest) =>
      api.post<OptionValueDto>(`/api/v1/admin/options/types/${typeId}/values`, body),
    onSuccess: () => onSuccess(`"${name.trim()}" created successfully.`),
    onError: (err: Error) => setError(err.message || 'Failed to create value.'),
  });

  const updateMutation = useMutation({
    mutationFn: (body: UpdateOptionValueRequest) =>
      api.patch<OptionValueDto>(`/api/v1/admin/options/values/${value!.id}`, body),
    onSuccess: () => onSuccess(`"${name.trim()}" updated successfully.`),
    onError: (err: Error) => setError(err.message || 'Failed to update value.'),
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isPending) onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isPending, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !displayName.trim()) {
      setError('Name and display name are required.');
      return;
    }

    const requiredFields = metadataSchema.filter((f) => f.required);
    for (const field of requiredFields) {
      const val = metadata[field.key];
      if (val == null || val === '') {
        setError(`"${field.label || field.key}" is required.`);
        return;
      }
    }

    if (isEdit) {
      updateMutation.mutate({
        name: name.trim(),
        displayName: displayName.trim(),
        metadata,
      });
    } else {
      createMutation.mutate({
        name: name.trim(),
        displayName: displayName.trim(),
        metadata,
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40" onClick={() => !isPending && onClose()} />
      <div className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-surface p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-text-primary">
          {isEdit ? 'Edit Value' : 'Add Value'}
        </h3>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          {isColorSwatch && (
            <fieldset className="rounded-lg border border-border p-3">
              <legend className="px-1.5 text-xs font-semibold uppercase tracking-wider text-brand-600">
                Color Library
              </legend>
              <p className="mb-2.5 text-[11px] text-text-tertiary">
                Search the color library to fill name and hex codes in one click. You can still edit everything below.
              </p>
              <ColorLibrarySearch
                disabled={isPending}
                onPick={(entry) => {
                  setName(entry.name);
                  setDisplayName(entry.name);
                  setMetadata((m) => ({
                    ...m,
                    hexCode: entry.hexCode,
                    ...(entry.hexCode2 ? { hexCode2: entry.hexCode2 } : {}),
                  }));
                }}
              />
            </fieldset>
          )}

          <fieldset className="rounded-lg border border-border p-3">
            <legend className="px-1.5 text-xs font-semibold uppercase tracking-wider text-brand-600">
              Variant Value
            </legend>
            <p className="mb-2.5 text-[11px] text-text-tertiary">
              This is the selectable option shown to customers (e.g. button label, dropdown item).
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-text-secondary">Name *</label>
                <Input value={name} onChange={(e) => handleNameChange(e.target.value)} placeholder="e.g. Forest Green" />
                <p className="mt-0.5 text-[11px] text-text-tertiary">Internal identifier &amp; SKU slug</p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-text-secondary">Display Name *</label>
                <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder={name || 'e.g. Forest Green'} />
                <p className="mt-0.5 text-[11px] text-text-tertiary">Label shown on storefront</p>
              </div>
            </div>
          </fieldset>

          {metadataSchema.length > 0 && (
            <fieldset className="rounded-lg border border-border p-3">
              <legend className="px-1.5 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
                Additional Properties
              </legend>
              <p className="mb-2.5 text-[11px] text-text-tertiary">
                Extra data for this value (e.g. hex color, measurements). Used by the designer and storefront display.
              </p>
              <DynamicMetadataForm
                schema={metadataSchema}
                value={metadata}
                onChange={setMetadata}
                disabled={isPending}
              />
              {isColorSwatch && (
                <p
                  className={`mt-2 text-[11px] ${
                    hexAutoFilled ? 'text-success-700' : 'text-text-tertiary'
                  }`}
                >
                  {hexAutoFilled
                    ? `Hex auto-detected from "${name}" via the color map. Edit to override.`
                    : !isValidHex6(metadata.hexCode) && name.trim().length > 0
                      ? `No hex match for "${name}" in the color map. Leave blank to skip, or enter a hex manually.`
                      : 'Hex code is optional - leave blank to skip color preview, or it will auto-fill when the name matches a known color.'}
                </p>
              )}
            </fieldset>
          )}

          {error && <AlertBanner type="error" message={error} />}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" size="sm" variant="outline" disabled={isPending} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={isPending}>
              {isPending ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Value'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Delete Value Confirmation Dialog
// ---------------------------------------------------------------------------

function DeleteValueDialog({
  value,
  onClose,
  onSuccess,
}: {
  value: OptionValueDto;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [error, setError] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/api/v1/admin/options/values/${value.id}`),
    onSuccess: () => onSuccess(),
    onError: (err: Error) => {
      setError(err.message || 'Failed to delete value.');
    },
  });

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !deleteMutation.isPending) onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [deleteMutation.isPending, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40" onClick={() => !deleteMutation.isPending && onClose()} />
      <div className="relative z-10 w-full max-w-md rounded-lg bg-surface p-6 shadow-xl">
        <div className="mb-1 flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-danger-100">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5 text-danger-600">
              <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-text-primary">Archive Value</h3>
        </div>

        <p className="mt-3 text-sm text-text-secondary">
          Are you sure you want to archive <strong className="text-text-primary">{value.name}</strong>?
          It will be deactivated and hidden from new selections. This value may be assigned to existing products.
        </p>

        {error && <div className="mt-3"><AlertBanner type="error" message={error} /></div>}

        <div className="mt-5 flex justify-end gap-3">
          <Button size="sm" variant="outline" disabled={deleteMutation.isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="danger"
            disabled={deleteMutation.isPending}
            onClick={() => { setError(null); deleteMutation.mutate(); }}
          >
            {deleteMutation.isPending ? 'Archiving...' : 'Yes, Archive'}
          </Button>
        </div>
      </div>
    </div>
  );
}
